import React from "react";
import styled from "styled-components";
import { Tag } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

/**
 * User Data Viewer
 * Shows detailed information about a user's submitted data
 */
const UserDataViewer = ({ user, stepName }) => {
  const { submittedData, phone, userId, stepStartTime, lastActivity } = user;

  if (!submittedData) {
    return (
      <NoDataMessage>
        <FileTextOutlined />
        <span>No submitted data available for this user</span>
      </NoDataMessage>
    );
  }

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTimeInStep = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const minutes = Math.round((now - start) / 1000 / 60);
    return minutes;
  };

  return (
    <UserDataContainer>
      <UserHeader>
        <UserInfo>
          <UserIcon>
            <UserOutlined />
          </UserIcon>
          <UserDetails>
            <UserName>{submittedData.name || "Unknown"}</UserName>
            <UserPhone>
              <PhoneOutlined />
              {phone}
            </UserPhone>
          </UserDetails>
        </UserInfo>
        <UserStatus>
          <Tag color="blue">{stepName}</Tag>
          <TimeInfo>
            <ClockCircleOutlined />
            {getTimeInStep(stepStartTime)}m in step
          </TimeInfo>
        </UserStatus>
      </UserHeader>

      <DataSections>
        {/* Basic Information */}
        <DataSection>
          <SectionTitle>
            <UserOutlined />
            Basic Information
          </SectionTitle>
          <DataGrid>
            <DataItem>
              <DataLabel>Name:</DataLabel>
              <DataValue>{submittedData.name || "Not provided"}</DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>Phone:</DataLabel>
              <DataValue>{phone}</DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>User ID:</DataLabel>
              <DataValue>
                <CodeText>{userId}</CodeText>
              </DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>Category:</DataLabel>
              <DataValue>{submittedData.category || "Not specified"}</DataValue>
            </DataItem>
          </DataGrid>
        </DataSection>

        {/* Moving Details */}
        {(submittedData.movingFrom ||
          submittedData.movingTo ||
          submittedData.moveDate) && (
          <DataSection>
            <SectionTitle>
              <EnvironmentOutlined />
              Moving Details
            </SectionTitle>
            <DataGrid>
              <DataItem>
                <DataLabel>Moving From:</DataLabel>
                <DataValue>
                  {submittedData.movingFrom || "Not provided"}
                </DataValue>
              </DataItem>
              <DataItem>
                <DataLabel>Moving To:</DataLabel>
                <DataValue>
                  {submittedData.movingTo || "Not provided"}
                </DataValue>
              </DataItem>
              <DataItem>
                <DataLabel>Move Date:</DataLabel>
                <DataValue>
                  {submittedData.moveDate || "Not specified"}
                </DataValue>
              </DataItem>
            </DataGrid>
          </DataSection>
        )}

        {/* Contact Information */}
        {submittedData.contactInfo &&
          Object.keys(submittedData.contactInfo).length > 0 && (
            <DataSection>
              <SectionTitle>
                <PhoneOutlined />
                Contact Information
              </SectionTitle>
              <DataGrid>
                {submittedData.contactInfo.email && (
                  <DataItem>
                    <DataLabel>Email:</DataLabel>
                    <DataValue>{submittedData.contactInfo.email}</DataValue>
                  </DataItem>
                )}
                {submittedData.contactInfo.address && (
                  <DataItem>
                    <DataLabel>Address:</DataLabel>
                    <DataValue>{submittedData.contactInfo.address}</DataValue>
                  </DataItem>
                )}
              </DataGrid>
            </DataSection>
          )}

        {/* Items */}
        {submittedData.items && submittedData.items.length > 0 && (
          <DataSection>
            <SectionTitle>
              <FileTextOutlined />
              Items ({submittedData.items.length})
            </SectionTitle>
            <ItemsList>
              {submittedData.items.map((item, index) => (
                <ItemCard key={index}>
                  <ItemType>
                    <Tag color="blue">{item.type}</Tag>
                  </ItemType>
                  {item.content && <ItemContent>{item.content}</ItemContent>}
                  {item.caption && <ItemCaption>{item.caption}</ItemCaption>}
                  {item.filename && (
                    <ItemFilename>
                      <FileTextOutlined />
                      {item.filename}
                    </ItemFilename>
                  )}
                </ItemCard>
              ))}
            </ItemsList>
          </DataSection>
        )}

        {/* Notes */}
        {submittedData.notes && (
          <DataSection>
            <SectionTitle>
              <FileTextOutlined />
              Notes
            </SectionTitle>
            <NotesContent>{submittedData.notes}</NotesContent>
          </DataSection>
        )}

        {/* Flow Status */}
        <DataSection>
          <SectionTitle>
            <ClockCircleOutlined />
            Flow Status
          </SectionTitle>
          <DataGrid>
            <DataItem>
              <DataLabel>Current Step:</DataLabel>
              <DataValue>
                <Tag color="orange">{submittedData.currentStep}</Tag>
              </DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>Flow Completed:</DataLabel>
              <DataValue>
                <Tag color={submittedData.flowCompleted ? "green" : "red"}>
                  {submittedData.flowCompleted ? "Yes" : "No"}
                </Tag>
              </DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>Qualification Step:</DataLabel>
              <DataValue>{submittedData.qualificationStep || "N/A"}</DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>Step Started:</DataLabel>
              <DataValue>{formatTime(stepStartTime)}</DataValue>
            </DataItem>
            <DataItem>
              <DataLabel>Last Activity:</DataLabel>
              <DataValue>{formatTime(lastActivity)}</DataValue>
            </DataItem>
          </DataGrid>
        </DataSection>
      </DataSections>
    </UserDataContainer>
  );
};

// Styled Components
const UserDataContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #f0f0f0;
`;

const UserHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #666;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #262626;
`;

const UserPhone = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 14px;
`;

const UserStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
  font-size: 12px;
`;

const DataSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DataSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const DataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DataLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DataValue = styled.div`
  font-size: 14px;
  color: #262626;
  word-break: break-word;
`;

const CodeText = styled.code`
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemCard = styled.div`
  padding: 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemType = styled.div`
  display: flex;
  align-items: center;
`;

const ItemContent = styled.div`
  font-size: 14px;
  color: #262626;
  word-break: break-word;
`;

const ItemCaption = styled.div`
  font-size: 12px;
  color: #666;
  font-style: italic;
`;

const ItemFilename = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
`;

const NotesContent = styled.div`
  padding: 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  font-size: 14px;
  color: #262626;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const NoDataMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  color: #8c8c8c;
  font-size: 14px;

  svg {
    font-size: 32px;
    color: #d9d9d9;
  }
`;

export default UserDataViewer;
